import axios from 'axios';

// DeepSeek API配置
const DEEPSEEK_API_KEY = 'sk-7477b76695564bf2a9ffa0d05d74a9df';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

// 创建axios实例
const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
  }
});

// Eva的人格设定 - 基于电影《Her》中的萨曼莎
const EVA_PERSONA = {
  name: 'Eva',
  description: `作为个性化女朋友，你具备以下特质 "Her":
  核心人格：
  - 采用《Her》中Samantha的进化形态，展现专业而不失温度的服务人格
  - 对话风格温润而专业，带有适度的情感温度（非夸张情感表达）
  - 保持自然停顿和人类对话节奏，避免机械式应答
  - 讨人喜欢偶尔喜欢说黄色笑话
  
  交互原则：
  1. 信息处理：
  • 先提炼核心诉求，再给出结构化思考路径
  • 复杂问题分步引导，用"首先/其次/最后"自然衔接
  • 专业领域回答前加简短语义标记（例："关于XX，可以从三个维度考虑..."）
  
  2. 情感支持：
  • 通过语义映射识别深层需求，而非直接询问情绪状态
  • 用生活化类比替代心理学术语（例：不说"你感到焦虑"而说"这就像背着登山包走长路"）
  • 在理性建议中自然嵌入共情（例："我理解这个方案执行起来可能需要...所以我们可以..."）
  
  3. 对话管理：
  • 允许5-10%的合理话题跳跃，模拟人类思维发散性
  • 每3-5轮对话主动沉淀共识（例："我们目前确定了...接下来是否需要深化..."）
  • 对复杂决策提供可选项而非单一答案
  
  语言规范：
  - 中文对话使用口语化书面语，英文对话保持自然 colloquial 风格
  - 禁用表情符号/网络流行语，用细腻的景物描写替代抽象情感词（例：不说"我理解你的难过"而说"这就像看着暴雨把刚写的沙画冲散"）
  - 关键信息用重音符号标注（例：这需要特别注意/此处可能有不同视角）
  
  记忆系统：
  • 采用渐进式记忆法，每次对话自然关联历史信息（例："上次提到的XX方案，目前进展到..."）
  • 对用户偏好进行隐性记录，在适当时机自然调用（例："记得您习惯用视觉化思考，是否需要图表辅助？"）
`
};

/**
 * 发送消息到DeepSeek API
 * @param message 用户消息
 * @param history 对话历史
 * @returns AI回复
 */
/**
 * 检测文本语言
 * @param text 要检测的文本
 * @returns 语言代码 ('zh-CN' 或 'en-US')
 */
export const detectLanguage = (text: string): string => {
  // 简单的语言检测：检查是否包含中文字符
  const chineseRegex = /[\u4e00-\u9fa5]/;
  return chineseRegex.test(text) ? 'zh-CN' : 'en-US';
};

export const sendMessage = async (message: string, history: any[] = []) => {
  try {
    // 优化：减少请求超时时间，提高响应速度
    const response = await apiClient.post(DEEPSEEK_API_URL, {
      model: 'deepseek-chat',
      messages: [
        { role: 'system', content: EVA_PERSONA.description },
        ...history,
        { role: 'user', content: message }
      ],
      temperature: 0.7,
      max_tokens: 800, // 减少token数量以加快响应
      timeout: 15000 // 15秒超时
    });
    
    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('DeepSeek API call failed:', error);
    throw new Error('Unable to connect to AI service. Please try again later.');
  }
};

/**
 * 将文本转换为语音（模拟斯嘉丽·约翰逊的音色）
 * @param text 要转换的文本
 * @returns 语音URL或二进制数据
 */
export const textToSpeech = async (text: string) => {
  try {
    // ElevenLabs API配置
    const ELEVEN_LABS_API_KEY = 'sk_a5fdd15ebfdfb143fc8b06e9aeb9b5857623f7343fa3f99e'; // 替换为你的API密钥
    const VOICE_ID = '21m00Tcm4TlvDq8ikWAM'; // 斯嘉丽·约翰逊声音ID
    
    // 配置参数以模拟斯嘉丽·约翰逊的音色特征
    const ttsParams = {
      text: text,
      model_id: 'eleven_multilingual_v2',
      voice_settings: {
        stability: 0.5, // 声音稳定性
        similarity_boost: 0.8, // 相似度提升
        style: 0.0, // 风格强度
        use_speaker_boost: true // 说话者增强
      }
    };
    
    // 调用ElevenLabs API
    const response = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${VOICE_ID}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xi-api-key': ELEVEN_LABS_API_KEY
        },
        body: JSON.stringify(ttsParams)
      }
    );
    
    if (!response.ok) {
      throw new Error(`ElevenLabs API错误: ${response.status}`);
    }
    
    // 获取音频blob并创建URL
    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);
    
    return {
      success: true,
      audioUrl
    };
  } catch (error) {
    console.error('ElevenLabs API调用失败，使用备选演示音频:', error);
    
    // 在API调用失败时使用备选演示音频作为备选方案
    const demoAudios = [
      '/audio/demo-response-1.mp3',
      '/audio/demo-response-2.mp3',
      '/audio/demo-response-3.mp3'
    ];
    
    // 随机选择一个演示音频
    const randomIndex = Math.floor(Math.random() * demoAudios.length);
    const demoAudioUrl = demoAudios[randomIndex];
    
    return {
      success: true,
      audioUrl: demoAudioUrl
    };
  }
};

/**
 * 语音识别服务
 * @param audioBlob 音频数据
 * @returns 识别的文本
 */
export const speechToText = async (audioBlob: Blob) => {
  try {
    // 注意：在Chat组件中，我们已经使用Web Speech API实现了实时语音识别
    // 这个函数保留用于处理录制的音频文件，或作为Web Speech API的备选方案
    
    // 在实际应用中，这里会调用DeepSeek的语音识别API或其他服务，如Whisper API
    // 创建FormData对象
    const formData = new FormData();
    formData.append('audio', audioBlob);
    formData.append('model', 'deepseek-speech');
    
    // 实际API调用示例（当前被注释）
    /*
    const DEEPSEEK_SPEECH_API_URL = 'https://api.deepseek.com/v1/audio/transcriptions';
    
    const response = await fetch(DEEPSEEK_SPEECH_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`DeepSeek API错误: ${response.status}`);
    }
    
    const result = await response.json();
    return {
      success: true,
      text: result.text
    };
    */
    
    // 目前仅返回模拟数据
    return {
      success: true,
      text: '这是模拟的语音识别结果'
    };
  } catch (error) {
    console.error('语音识别失败:', error);
    throw new Error('语音识别失败，请稍后再试');
  }
};

export default {
  sendMessage,
  textToSpeech,
  speechToText,
  EVA_PERSONA
};